import * as Highcharts from "highcharts";
import React from "react";
import HighchartsReact from "highcharts-react-official";
import { ITimeChartsInfo } from "ts/parsers/analytics";
import { timeTakenTooltipFormatter } from "../../../../formatters/analytics";
import {
  centerHorizontalBarTooltip,
  defaultIndividualUnitsSettings,
  defaultTooltipSize,
  defaultTooltip,
} from "../../../../utils/charts";
import colors from "../../../../styles/colors";
import { createGradient } from "../../../../formatters/analytics";

interface ITimeChartProps {
  times: ITimeChartsInfo[];
  maxAvgTime: number;
  showYAxis: boolean;
  isFilterActive?: boolean;
}

export const TimeChart: React.FC<ITimeChartProps> = ({ times, maxAvgTime, showYAxis, isFilterActive }) => (
  <HighchartsReact
    highcharts={Highcharts}
    options={timeChartOptions(times, maxAvgTime, showYAxis, isFilterActive)}
    containerProps={{ style: { width: "100%" } }}
  />
);

const timeChartOptions = (
  times: ITimeChartsInfo[],
  maxAvgTime: number,
  showYAxis: boolean,
  isFilterActive?: boolean
): Highcharts.Options => ({
  chart: {
    backgroundColor: "transparent",
    type: "bar",
  },
  plotOptions: {
    bar: {
      borderRadius: 5,
      pointPadding: 0,
    },
  },
  series: [
    {
      data: times.map((time: ITimeChartsInfo) => time["Average Time"]),
      name: "Average Time Spent",
      /* tslint:disable-next-line */
      color: isFilterActive
        ? createGradient(colors.chartFilteredGray100, colors.chartFilteredGray10)
        : createGradient(colors.chartLightBlue100, colors.chartLightBlue10),
    } as Highcharts.SeriesBarOptions,
    isFilterActive &&
      ({
        data: times.map((time: ITimeChartsInfo) => time.filtered),
        name: "Filtered",
        /* tslint:disable-next-line */
        color: createGradient(colors.chartLightBlue100, colors.chartLightBlue10),
      } as Highcharts.SeriesBarOptions),
  ].filter(Boolean) as Highcharts.SeriesBarOptions[],
  tooltip: {
    formatter() {
      const label = this.x!;
      const timeValue = this.y!;
      const isFiltered = isFilterActive && this.series.name === "Filtered";
      const textColor = this.series.color as Highcharts.GradientColorObject;
      return timeTakenTooltipFormatter(timeValue, label, textColor.stops[0][1], isFiltered);
    },
    positioner(...params) {
      return centerHorizontalBarTooltip(this.chart)(...params);
    },
    ...defaultTooltip,
  },
  xAxis: {
    categories: times.map((time: ITimeChartsInfo) => time.name),
    gridLineColor: "transparent",
    labels: {
      enabled: false,
    },
    lineColor: "transparent",
  },
  yAxis: {
    labels: {
      formatter() {
        return showYAxis ? `<b>${(Number(this.value) / 60).toFixed(2)} min</b>` : "";
      },
      ...defaultTooltipSize,
    },
    lineColor: colors.chartAxisGrey100,
    lineWidth: showYAxis ? 3 : 0,
    max: maxAvgTime,
    opposite: true,
    tickInterval: maxAvgTime,
    title: {
      text: null,
    },
  },
  ...defaultIndividualUnitsSettings,
});
